import { PuzzleSettings, type PuzzleSettingsDto } from 'endchess-contracts';

export type PuzzleRatingBounds = {
  min: number;
  max: number;
};

export type PuzzleDifficultyBand = 'easy' | 'medium' | 'hard';

export const DEFAULT_PLAYER_RATING = 1500;

const EASY_BELOW_MIN = 300;
const EASY_BELOW_MAX = 100;
const MEDIUM_BELOW = 100;
const MEDIUM_ABOVE = 100;
const HARD_ABOVE_MIN = 100;
const HARD_ABOVE_MAX = 300;

export const clampRating = (
  value: number,
  bounds: PuzzleRatingBounds,
): number => Math.min(bounds.max, Math.max(bounds.min, value));

export const mediumRangeForRating = (
  playerRating: number,
  bounds: PuzzleRatingBounds,
): [number, number] => {
  const rating = Math.round(playerRating);
  return [
    clampRating(rating - MEDIUM_BELOW, bounds),
    clampRating(rating + MEDIUM_ABOVE, bounds),
  ];
};

export const defaultRangeForRating = (
  playerRating: number,
  bounds: PuzzleRatingBounds,
): [number, number] => mediumRangeForRating(playerRating, bounds);

/** Band below the player's puzzle rating. */
export const easyRangeForRating = (
  playerRating: number,
  bounds: PuzzleRatingBounds,
): [number, number] => {
  const rating = Math.round(playerRating);
  return [
    clampRating(rating - EASY_BELOW_MIN, bounds),
    clampRating(rating - EASY_BELOW_MAX, bounds),
  ];
};

/** Band above the player's puzzle rating. */
export const hardRangeForRating = (
  playerRating: number,
  bounds: PuzzleRatingBounds,
): [number, number] => {
  const rating = Math.round(playerRating);
  return [
    clampRating(rating + HARD_ABOVE_MIN, bounds),
    clampRating(rating + HARD_ABOVE_MAX, bounds),
  ];
};

export const rangeForBand = (
  band: PuzzleDifficultyBand,
  playerRating: number,
  bounds: PuzzleRatingBounds,
): [number, number] => {
  switch (band) {
    case 'easy':
      return easyRangeForRating(playerRating, bounds);
    case 'hard':
      return hardRangeForRating(playerRating, bounds);
    default:
      return mediumRangeForRating(playerRating, bounds);
  }
};

export const buildPuzzleSettings = (
  ratingMin: number,
  ratingMax: number,
): string => {
  const settings: PuzzleSettingsDto = {
    solvedStatuses: [PuzzleSettings.SolvedStatus.UNSOLVED],
    ratingMin,
    ratingMax,
  };
  return JSON.stringify(settings);
};
